import {
  FRAGMENT_SHADER_SOURCE,
  SIMULATION_SHADER_SOURCE,
  VERTEX_SHADER_SOURCE,
} from './loginShaderSources';

const TRAIL_POINT_COUNT = 12;
const POINTER_IDLE_TIMEOUT = 1000;
const SIMULATION_STEPS = 2;

function clampUnit(value) {
  return Math.max(0, Math.min(1, value));
}

function clampVelocity(value) {
  return Math.max(-1.45, Math.min(1.45, value));
}

function createShader(gl, type, source) {
  const shader = gl.createShader(type);

  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const error = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(error || 'Failed to compile login shader.');
  }

  return shader;
}

function createProgram(gl, fragmentSource) {
  const vertexShader = createShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER_SOURCE);
  const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
  const program = gl.createProgram();

  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  gl.deleteShader(vertexShader);
  gl.deleteShader(fragmentShader);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const error = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw new Error(error || 'Failed to link login shader.');
  }

  return program;
}

function createStateData(width, height) {
  const data = new Uint8Array(width * height * 4);

  for (let index = 0; index < data.length; index += 4) {
    data[index + 1] = 128;
    data[index + 2] = 128;
  }

  return data;
}

function createStateTarget(gl, width, height) {
  const texture = gl.createTexture();
  const framebuffer = gl.createFramebuffer();

  if (!texture || !framebuffer) {
    throw new Error('Failed to allocate login shader feedback target.');
  }

  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texImage2D(
    gl.TEXTURE_2D,
    0,
    gl.RGBA,
    width,
    height,
    0,
    gl.RGBA,
    gl.UNSIGNED_BYTE,
    createStateData(width, height),
  );

  gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
  gl.framebufferTexture2D(
    gl.FRAMEBUFFER,
    gl.COLOR_ATTACHMENT0,
    gl.TEXTURE_2D,
    texture,
    0,
  );

  if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
    gl.deleteTexture(texture);
    gl.deleteFramebuffer(framebuffer);
    throw new Error('Login shader feedback target is incomplete.');
  }

  return { texture, framebuffer };
}

function setUniformColor(gl, location, color) {
  gl.uniform3f(location, color[0], color[1], color[2]);
}

function getUniforms(gl, program, names) {
  return Object.fromEntries(names.map((name) => [name, gl.getUniformLocation(program, name)]));
}

export function createLoginShaderRuntime({ canvas, gl, palette }) {
  const renderProgram = createProgram(gl, FRAGMENT_SHADER_SOURCE);
  const simulationProgram = createProgram(gl, SIMULATION_SHADER_SOURCE);
  const positionBuffer = gl.createBuffer();

  if (!positionBuffer) {
    throw new Error('Failed to allocate login shader geometry.');
  }

  const renderAttribute = gl.getAttribLocation(renderProgram, 'a_position');
  const simulationAttribute = gl.getAttribLocation(simulationProgram, 'a_position');
  const renderUniforms = getUniforms(gl, renderProgram, [
    'u_resolution',
    'u_time',
    'u_bg',
    'u_haze',
    'u_wisp',
    'u_accent',
    'u_state',
    'u_state_resolution',
    'u_pointer',
    'u_velocity',
    'u_pointer_strength',
  ]);
  const simulationUniforms = getUniforms(gl, simulationProgram, [
    'u_resolution',
    'u_time',
    'u_state',
    'u_injection_gain',
    'u_pointer',
    'u_velocity',
    'u_pointer_strength',
    'u_wake',
    'u_trail[0]',
    'u_trail_motion[0]',
  ]);
  const pointer = {
    x: 0.5,
    y: 0.5,
    targetX: 0.5,
    targetY: 0.5,
    velocityX: 0,
    velocityY: 0,
    targetVelocityX: 0,
    targetVelocityY: 0,
    strength: 0,
    targetStrength: 0,
    wake: 0,
    targetWake: 0,
    lastMove: 0,
    lastTrailAt: 0,
  };
  const trailPoints = Array.from({ length: TRAIL_POINT_COUNT }, () => ({
    x: 0.5,
    y: 0.5,
    bornAt: -10,
    strength: 0,
    motionX: 0,
    motionY: 0,
  }));
  const trailUniform = new Float32Array(TRAIL_POINT_COUNT * 4);
  const trailMotionUniform = new Float32Array(TRAIL_POINT_COUNT * 2);
  let trailIndex = 0;
  let stateTargets = [];
  let stateIndex = 0;
  let simulationWidth = 0;
  let simulationHeight = 0;

  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 3, -1, -1, 3]),
    gl.STATIC_DRAW,
  );

  gl.disable(gl.DEPTH_TEST);
  gl.disable(gl.STENCIL_TEST);

  const bindGeometry = (program, attribute) => {
    gl.useProgram(program);
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.enableVertexAttribArray(attribute);
    gl.vertexAttribPointer(attribute, 2, gl.FLOAT, false, 0, 0);
  };

  const deleteStateTargets = () => {
    stateTargets.forEach((target) => {
      gl.deleteTexture(target.texture);
      gl.deleteFramebuffer(target.framebuffer);
    });
    stateTargets = [];
  };

  const resize = () => {
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 1.5) * 0.75;
    const width = Math.max(1, Math.floor(canvas.clientWidth * pixelRatio));
    const height = Math.max(1, Math.floor(canvas.clientHeight * pixelRatio));
    const simulationPixelRatio = Math.min(window.devicePixelRatio || 1, 1.25) * 0.38;
    const nextSimulationWidth = Math.max(96, Math.floor(canvas.clientWidth * simulationPixelRatio));
    const nextSimulationHeight = Math.max(96, Math.floor(canvas.clientHeight * simulationPixelRatio));

    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }

    if (simulationWidth !== nextSimulationWidth || simulationHeight !== nextSimulationHeight) {
      deleteStateTargets();
      simulationWidth = nextSimulationWidth;
      simulationHeight = nextSimulationHeight;
      stateTargets = [
        createStateTarget(gl, simulationWidth, simulationHeight),
        createStateTarget(gl, simulationWidth, simulationHeight),
      ];
      stateIndex = 0;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    }
  };

  const updatePointer = (timestamp = 0, reducedMotion = false) => {
    if (timestamp - pointer.lastMove > POINTER_IDLE_TIMEOUT) {
      pointer.targetStrength = 0;
      pointer.targetVelocityX = 0;
      pointer.targetVelocityY = 0;
      pointer.targetWake = 0;
    }

    const follow = reducedMotion ? 1 : 0.34;
    pointer.x += (pointer.targetX - pointer.x) * follow;
    pointer.y += (pointer.targetY - pointer.y) * follow;
    pointer.velocityX += (pointer.targetVelocityX - pointer.velocityX) * 0.3;
    pointer.velocityY += (pointer.targetVelocityY - pointer.velocityY) * 0.3;
    pointer.strength += (pointer.targetStrength - pointer.strength) * 0.3;
    pointer.wake += (pointer.targetWake - pointer.wake) * 0.18;
  };

  const updateTrailUniform = (timestamp = 0) => {
    const seconds = timestamp * 0.001;

    trailPoints.forEach((trailPoint, index) => {
      const age = Math.max(0, seconds - trailPoint.bornAt);
      const offset = index * 4;
      const motionOffset = index * 2;

      trailUniform[offset] = trailPoint.x;
      trailUniform[offset + 1] = trailPoint.y;
      trailUniform[offset + 2] = age;
      trailUniform[offset + 3] = trailPoint.strength * Math.exp(-age * 0.54);
      trailMotionUniform[motionOffset] = trailPoint.motionX;
      trailMotionUniform[motionOffset + 1] = trailPoint.motionY;
    });
  };

  const simulatePass = (timestamp = 0, injectionGain = 1) => {
    const readTarget = stateTargets[stateIndex];
    const writeIndex = 1 - stateIndex;
    const writeTarget = stateTargets[writeIndex];

    bindGeometry(simulationProgram, simulationAttribute);
    gl.viewport(0, 0, simulationWidth, simulationHeight);
    gl.bindFramebuffer(gl.FRAMEBUFFER, writeTarget.framebuffer);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, readTarget.texture);
    gl.uniform1i(simulationUniforms.u_state, 0);
    gl.uniform2f(simulationUniforms.u_resolution, simulationWidth, simulationHeight);
    gl.uniform1f(simulationUniforms.u_time, timestamp * 0.001);
    gl.uniform1f(simulationUniforms.u_injection_gain, injectionGain);
    gl.uniform2f(simulationUniforms.u_pointer, pointer.x, pointer.y);
    gl.uniform2f(simulationUniforms.u_velocity, pointer.velocityX, pointer.velocityY);
    gl.uniform1f(simulationUniforms.u_pointer_strength, pointer.strength);
    gl.uniform1f(simulationUniforms.u_wake, pointer.wake);
    gl.uniform4fv(simulationUniforms['u_trail[0]'], trailUniform);
    gl.uniform2fv(simulationUniforms['u_trail_motion[0]'], trailMotionUniform);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    stateIndex = writeIndex;
  };

  const simulate = (timestamp = 0, reducedMotion = false) => {
    if (reducedMotion || stateTargets.length < 2) {
      return;
    }

    for (let step = 0; step < SIMULATION_STEPS; step += 1) {
      const injectionGain = step === 0 ? 1 : 0;
      simulatePass(timestamp + step * 8, injectionGain);
    }
  };

  const render = (timestamp = 0, reducedMotion = false) => {
    const stateTarget = stateTargets[stateIndex];

    bindGeometry(renderProgram, renderAttribute);
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, stateTarget?.texture || null);
    gl.uniform1i(renderUniforms.u_state, 0);
    gl.uniform2f(renderUniforms.u_resolution, canvas.width, canvas.height);
    gl.uniform2f(renderUniforms.u_state_resolution, simulationWidth, simulationHeight);
    gl.uniform1f(renderUniforms.u_time, reducedMotion ? 0 : timestamp * 0.001);
    gl.uniform2f(renderUniforms.u_pointer, pointer.x, pointer.y);
    gl.uniform2f(renderUniforms.u_velocity, pointer.velocityX, pointer.velocityY);
    gl.uniform1f(renderUniforms.u_pointer_strength, reducedMotion ? 0 : pointer.strength);
    setUniformColor(gl, renderUniforms.u_bg, palette.bg);
    setUniformColor(gl, renderUniforms.u_haze, palette.haze);
    setUniformColor(gl, renderUniforms.u_wisp, palette.wisp);
    setUniformColor(gl, renderUniforms.u_accent, palette.accent);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  };

  const drawFrame = (timestamp = 0, { reducedMotion = false } = {}) => {
    resize();
    updatePointer(timestamp, reducedMotion);
    updateTrailUniform(timestamp);
    simulate(timestamp, reducedMotion);
    render(timestamp, reducedMotion);
  };

  const handlePointerMove = (event) => {
    const bounds = canvas.getBoundingClientRect();
    const now = performance.now();

    if (bounds.width <= 0 || bounds.height <= 0) {
      return now;
    }

    const nextX = clampUnit((event.clientX - bounds.left) / bounds.width);
    const nextY = clampUnit(1 - ((event.clientY - bounds.top) / bounds.height));
    const previousMove = pointer.lastMove || now - 16;
    const elapsed = Math.max(16, now - previousMove);
    const deltaX = nextX - pointer.targetX;
    const deltaY = nextY - pointer.targetY;
    const velocityX = clampVelocity(deltaX / (elapsed / 1000));
    const velocityY = clampVelocity(deltaY / (elapsed / 1000));
    const speed = Math.min(1.7, Math.hypot(velocityX, velocityY));

    pointer.targetX = nextX;
    pointer.targetY = nextY;
    pointer.targetVelocityX = velocityX;
    pointer.targetVelocityY = velocityY;
    pointer.targetStrength = Math.min(0.92, 0.22 + speed * 0.36);
    pointer.targetWake = Math.max(pointer.targetWake * 0.62, speed * 0.56);
    pointer.lastMove = now;

    if (speed > 0.01 && now - pointer.lastTrailAt > 14) {
      trailIndex = (trailIndex + 1) % TRAIL_POINT_COUNT;
      trailPoints[trailIndex] = {
        x: nextX,
        y: nextY,
        bornAt: now * 0.001,
        strength: Math.min(0.94, 0.22 + speed * 0.36),
        motionX: velocityX,
        motionY: velocityY,
      };
      pointer.lastTrailAt = now;
    }

    return now;
  };

  const handlePointerLeave = () => {
    pointer.targetStrength = 0;
    pointer.targetWake = 0;
    pointer.targetVelocityX = 0;
    pointer.targetVelocityY = 0;
  };

  const dispose = () => {
    deleteStateTargets();
    gl.deleteBuffer(positionBuffer);
    gl.deleteProgram(renderProgram);
    gl.deleteProgram(simulationProgram);
  };

  return {
    dispose,
    drawFrame,
    handlePointerLeave,
    handlePointerMove,
  };
}
